import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VERSION } from '../../app.constants';
import { JhiLanguageService } from '../../shared/core-utils';
import { LoginService } from '../../shared/login/login.service';
import { LoginPageDetails } from '../../shared/login/login.model';
import { UserProfileModel } from '../../entities/user-profile/model/user-profile.model';
import zxcvbn from 'zxcvbn';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'jhi-forgot-password',
    templateUrl: './reset-password.component.html',
    styleUrls: [
        '../login.component.scss'
    ],
    encapsulation: ViewEncapsulation.None
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

    passwordStrength: any;
    version = VERSION;
    pageDetails: LoginPageDetails;
    model: UserProfileModel = { username: '', email: '' };
    strengthMsg = '';
    progressBarClass = '';
    progressWidth = '0%';

    isFormValid = true;
    isLoading = false;
    errorMessage: string;
    errorFields = [];

    constructor(private route: ActivatedRoute,
                private languageService: JhiLanguageService,
                private loginService: LoginService,
                private translateService: TranslateService,
                private router: Router,
    ) {
        document.body.classList.add('body-container');
    }

    ngOnInit(): void {
        const token = this.route.snapshot.paramMap.get('token');
        this.loginService.getLoginPageDetails().toPromise().then((response) => {
            this.pageDetails = response;
        });

        this.loginService.getResetPageDetails(token).toPromise().then((response) => {
            this.model = response;
        }).catch((response) => {
            this.router.navigate(['/login']);
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('body-container');
    }

    doPostResetAction() {
        this.clearErrors();
        this.isLoading = true;
        const token = this.route.snapshot.paramMap.get('token');
        return this.loginService.submitResetPassword(token, this.model).toPromise().then((response) => {
            this.isLoading = false;
            this.router.navigate(['/login']);
        }).catch((response) => {
            this.applyValidationErrors(response ? response.error.errors : {});
        });
    }

    assessPasswordStrength(evt) {
        const password = evt.target.value;
        if (password === '') {
            return {
                valid: false,
                message: this.translateService.instant('login.reset.error.pw.empty')
            };
        }

        const result = zxcvbn(password),
            score = result.score,
            message = result.feedback.warning || this.translateService.instant('login.reset.message.pw.weak');

        this.strengthMsg = this.translateService.instant('login.reset.pw.bar.label.weak');
        this.progressBarClass = 'progress-bar-danger';
        switch (score) {
        case 0:
            this.progressWidth = '20%';
            break;
        case 1:
            this.progressWidth = '35%';
            break;
        case 2:
            this.progressWidth = '50%';
            break;
        case 3:
            this.progressWidth = '75%';
            this.progressBarClass = 'progress-bar-warning';
            this.strengthMsg = this.translateService.instant('login.reset.pw.bar.label.acceptable');
            break;
        case 4:
            this.progressWidth = '100%';
            this.progressBarClass = 'progress-bar-success';
            this.strengthMsg = this.translateService.instant('login.reset.pw.bar.label.strong');
            break;
        }

        // We will treat the password as an invalid one if the score is less than 3
        if (score < 3) {
            this.displayClientError(message);
        } else {
            this.errorMessage = '';
            this.isFormValid = true;
        }

        return {
            valid: true,
            message: ''
        };
    }

    private displayClientError(errorMsg) {
        this.errorMessage = errorMsg;
        this.isFormValid = false;
        this.isLoading = false;
    }

    private applyValidationErrors(errors) {
        let errorMessage = '';

        Object.keys(errors).forEach((fieldName) => {
            this.errorFields.push(fieldName);
            errorMessage += errorMessage ? (' ' + errors[fieldName]) : errors[fieldName];

        });
        this.displayClientError(errorMessage);
    }

    private clearErrors() {
        // Double check if form is actually invalid, as this function may occasionally be called unnecessarily
        if (!this.isFormValid) {
            // Clear client errors
            this.errorMessage = '';
            this.isFormValid = true;
            this.errorFields = [];
        }
    }
}
